import { Rectangle } from '@/components/VisualAssetEditor/types';

export interface CloudinaryOverlayParams {
  width: number;
  height: number;
  x: number;
  y: number;
  color: string;
  opacity?: number;
  borderWidth?: number;
  borderColor?: string;
}

export class CloudinaryOverlays {
  /**
   * Generates a Cloudinary URL with rectangle overlay
   * Based on the syntax: l_one_pixel.png,w_60,h_60,e_colorize,co_rgb:D6E265,g_face,o_50
   */
  static generateOverlayUrl(
    baseUrl: string,
    overlayParams: CloudinaryOverlayParams
  ): string {
    const { width, height, x, y, color, opacity = 15, borderWidth = 2, borderColor } = overlayParams;
    
    // Remove # from color if present
    const cleanColor = color.replace('#', '');
    const cleanBorderColor = (borderColor || color).replace('#', '');
    
    // Build overlay transformation using the exact working pattern with border
    const overlayTransform = [
      'l_one_pixel_gidbyp',
      `w_${Math.round(width)}`,
      `h_${Math.round(height)}`,
      'e_colorize',
      `co_rgb:${cleanColor}`,
      `o_${opacity}`,
      `bo_${borderWidth}px_solid_rgb:${cleanBorderColor}`,
      'fl_layer_apply',
      'g_north_west',
      `x_${Math.round(x)}`,
      `y_${Math.round(y)}`
    ].join(',');
    
    // Parse the URL to insert overlay transformation correctly
    const uploadIndex = baseUrl.indexOf('/upload/');
    if (uploadIndex === -1) return baseUrl;
    
    const beforeUpload = baseUrl.substring(0, uploadIndex + 8); // includes '/upload/'
    const afterUpload = baseUrl.substring(uploadIndex + 8);
    
    // Find where version starts (v followed by digits)
    const versionMatch = afterUpload.match(/\/v\d+\//);
    
    if (versionMatch) {
      // Split at version
      const versionIndex = afterUpload.indexOf(versionMatch[0]);
      const transformsPart = afterUpload.substring(0, versionIndex);
      const versionAndRest = afterUpload.substring(versionIndex + 1); // remove leading slash
      
      if (transformsPart.length > 0) {
        // Add overlay to existing transformations
        return `${beforeUpload}${transformsPart}/${overlayTransform}/${versionAndRest}`;
      } else {
        // No existing transformations
        return `${beforeUpload}${overlayTransform}/${versionAndRest}`;
      }
    } else {
      // No version in URL, just append overlay
      return `${beforeUpload}${overlayTransform}/${afterUpload}`;
    }
  }

  /**
   * Converts display rectangle coordinates to image coordinates
   */
  static convertDisplayToImageCoords(
    rectangle: Rectangle,
    imageElement: HTMLImageElement,
    containerElement: HTMLElement,
    zoom: number,
    panX: number,
    panY: number
  ): CloudinaryOverlayParams {
    const im = imageElement.getBoundingClientRect();

    // Use per-axis scale to handle any potential non-uniform transforms
    const scaleX = im.width / imageElement.naturalWidth;
    const scaleY = im.height / imageElement.naturalHeight;

    // Convert rectangle coordinates from container space to image coordinates
    // Account for image position relative to container and scaling
    const imageX = (rectangle.x - (im.left - containerElement.getBoundingClientRect().left)) / scaleX;
    const imageY = (rectangle.y - (im.top - containerElement.getBoundingClientRect().top)) / scaleY;
    const imageWidth = rectangle.width / scaleX;
    const imageHeight = rectangle.height / scaleY;
    
    return {
      x: Math.round(imageX),
      y: Math.round(imageY),
      width: Math.round(imageWidth),
      height: Math.round(imageHeight),
      color: rectangle.color || 'D3E472',
      opacity: 15,
      borderWidth: 2,
      borderColor: rectangle.color || 'D3E472'
    };
  }

  /**
   * Generates overlay URL for multiple rectangles
   */
  static generateMultipleOverlaysUrl(
    baseUrl: string,
    overlays: CloudinaryOverlayParams[]
  ): string {
    let result = baseUrl;
    
    for (const overlay of overlays) {
      result = this.generateOverlayUrl(result, overlay);
    }
    
    return result;
  }
}